import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaExternalLinkAlt, FaGithub, FaRobot, FaRocket } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import "./Project.css";
import projimage1 from "../../Assets/Mainpage/projimage1.png";
import projimage2 from "../../Assets/Mainpage/projimage2.png";
import projimage4 from "../../Assets/Mainpage/projimage4.png";
import projimage5 from "../../Assets/Mainpage/projimage5.png";
import projimage6 from "../../Assets/Mainpage/projimage6.png";
import projimage7 from "../../Assets/Mainpage/projimage7.png";

const Projects = () => {
  const [activeTab, setActiveTab] = useState("web");
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const projects = {
    web: [
      {
        title: "MERN Web Market (No Live Demo Yet)",
        description:
          "Starter Fullstack MERN web market with user authentication, product management, shopping cart, payment integration and admin dashboard",
        technologies: ["MongoDB", "Express.js", "React.js", "Node.js"],
        imgUrl: projimage1,
        github: "https://github.com/WillyOctz/MERN-Web_Market",
        demo: "",
      },
      {
        title: "Restaurant WebPage React/Vite Sample (No Live Demo Yet)",
        description:
          "A sample restaurant webpage built with React/Vite for showcasing frontend and design skills",
        technologies: ["React.js", "Vite", "Tailwind CSS"],
        imgUrl: projimage2,
        github:
          "https://github.com/WillyOctz/Restaurant-WebPage-React-Vite_Sample",
        demo: "#",
      },
      {
        title: "Catatdulu Money Management App (Completed)",
        description:
          "A money management app built for managing personal finances",
        technologies: ["Java Spring Boot", "MySQL", "React/Next.js"],
        imgUrl: projimage5,
        github: "https://github.com/WillyOctz/catatdulu-backend",
        demo: "https://catatdulu-frontend.vercel.app/",
      },
      {
        title: "Financial Advisor AI (Completed)",
        description:
          "A financial advisor app built for providing personalized financial advice, insights, forecasting, predictions and metrics to track and manage personal finances",
        technologies: [
          "Python",
          "PostgreSQL",
          "React/Next.js",
          "GROK AI",
          "HuggingFace Transformers",
          "Gemini",
          "LangChain",
          "FastAPI",
          "Pandas",
          "Docker",
          "Sentry",
          "SQLAlchemy",
          "Argon",
          "Pgvector",
          "Others"
        ],
        imgUrl: projimage6,
        github: "https://github.com/WillyOctz/Financial-Advisor-AI",
        demo: "https://financial-advisor-ai-eight.vercel.app/",
      },
      {
        title: "E-Commerce Shopify (Incomplete)",
        description:
          "An e-commerce website built like shopify with product listing, shopping cart, checkout, payment integration, product and admin dashboard and other function like shopify. (This project is still in development and currently incomplete)",
        technologies: [
          "React/Next.js",
          "Prisma",
          "MongoDB",
          "Kafka",
          "Redis",
          "Express.js",
          "Node.js",
          "Stripe",
          "Imagekit",
          "Google OAuth",
        ],
        imgUrl: projimage7,
        github: "https://github.com/WillyOctz/MultiVendor-Ecommerce-Shopify",
        demo: "#",
      },
    ],
    ml: [
      {
        title: "Food Nutrition Predictor (No Live Demo Yet)",
        description:
          "Trained Machine Learning model to predict nutritional content of food,ingredients and drinks",
        technologies: ["Python", "Pandas", "NumPy", "Scikit-learn", "Keras"],
        imgUrl: projimage4,
        github: "https://github.com/WillyOctz/MERN-Web_Market",
        demo: "#",
      },
    ],
  };

  const tabs = [
    { id: "web", label: "Web Development", icon: <FaRobot /> },
    { id: "ml", label: "Machine Learning", icon: <FaRocket /> },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const ProjectCard = ({ project, index }) => (
    <motion.div
      className="project-card glass-card"
      variants={cardVariants}
      whileHover={cardVariants}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      {/* Image Container */}
      <div className="project-image-container">
        <img
          src={project.imgUrl}
          alt={project.title}
          className="project-image"
        />
        <motion.div
          className="project-overlay"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="project-links">
            <motion.a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaGithub />
            </motion.a>
            <motion.a
              href={project.demo}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
              whileHover={{ scale: 1.1, rotate: -5 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaExternalLinkAlt />
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="project-content">
        <h3 className="project-title">{project.title}</h3>
        <p className="project-description">{project.description}</p>

        {/* Technologies Used */}
        <div className="project-technologies">
          {project.technologies.map((tech, i) => (
            <motion.span
              key={i}
              className="tech-badge"
              initial={{ opacity: 0, scale: 0 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: index * 0.1 + i * 0.05 }}
            >
              {tech}
            </motion.span>
          ))}
        </div>
      </div>
    </motion.div>
  );

  return (
    <section className="projects-section" id="project" ref={ref}>
      <div className="container">
        {/* Header */}
        <motion.div
          className="projects-header"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="section-badge"
            initial={{ opacity: 0, scale: 0 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <HiSparkles className="badge-icon" />
            Projects
          </motion.div>
          <h2 className="sectioin-title">
            Featured <span className="gradient-text">Projects</span>
          </h2>
        </motion.div>

        {/* Tabs */}
        <motion.div
          className="project-tabs"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {activeTab === tab.id && (
                <motion.div
                  className="tab-indicator"
                  layoutId="activeTab"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Project Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            className="projects-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: -20 }}
          >
            {projects[activeTab].map((project, index) => (
              <ProjectCard key={index} project={project} index={index} />
            ))}
          </motion.div>
        </AnimatePresence>

        {/* View More */}
        <motion.div
          className="projects-footer"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <motion.button
            className="btn-modern btn-secondary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View More
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
